import xor from 'lodash/xor';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { MenuCheckboxItem } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { Venue } from 'common/api-types/director';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { LoadingBar } from 'common/components/cookbook/LoadingBar';
import { ModalSpinner } from 'common/components/cookbook/Modals/ModalSpinner';
import { SearchInput } from 'common/components/cookbook/Search/SearchInput';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useVenuesQuery } from 'common/hooks/useVenuesQuery';

import { ThinScrollbarContainer } from '../../ThinScrollbarContainer';

const ItemList = styled(ThinScrollbarContainer)`
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > * {
    flex-shrink: 0;
  }
`;

const SubTitle = styled.div`
  ${typographyCss.footnote}
  color: ${color.textSubdued};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledEmpty = styled(Empty)`
  margin: 16px 0;
`;

type VenueDisplayField = keyof Pick<Venue, 'name' | 'code'>;

type Props = {
  selectedVenues: string[];
  onSelectedVenuesChange: (venues: string[]) => void;
  franchises?: string[];
  valueKey?: 'id' | 'code';
  title: VenueDisplayField;
  subtitle: VenueDisplayField;
};

export const VenuesFilter = ({
  selectedVenues,
  franchises = ['woltmarket', 'woltmarket_virtual_store'],
  valueKey = 'id',
  title,
  subtitle,
  onSelectedVenuesChange,
}: Props) => {
  const formatMessage = useFormatMessage();
  const countryCodes = useSelectedCountryCodes();
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const {
    data: venuesData,
    isRefetching,
    isLoading,
    isPlaceholderData,
    error,
  } = useVenuesQuery({
    countryCodes,
    q: searchQuery && searchQuery.length > 2 ? searchQuery : undefined,
  });

  const venues = useMemo(
    () =>
      (franchises.length > 0
        ? venuesData?.items.filter(
            (venue) => venue.franchise && franchises.includes(venue.franchise),
          )
        : venuesData?.items) ?? [],
    [venuesData?.items, franchises],
  );

  return (
    <Stack gap="regularMedium">
      <SearchInput
        value={searchQuery}
        onChange={(value) => setSearchQuery(value)}
        data-test-id="venue-search-input"
        autoFocus
      />
      {isLoading && <ModalSpinner />}
      {isRefetching && <LoadingBar />}
      <ApiErrorNotification error={error} />
      {venues && !isPlaceholderData && (
        <ItemList role="menu">
          {venues.map((venue) => (
            <MenuCheckboxItem
              key={venue.id}
              data-test-id={venue[valueKey]}
              checked={selectedVenues.includes(venue[valueKey])}
              onClick={() => onSelectedVenuesChange(xor(selectedVenues, [venue[valueKey]]))}
            >
              {venue[title]}
              <SubTitle>{venue[subtitle]}</SubTitle>
            </MenuCheckboxItem>
          ))}
          {venues.length === 0 && (
            <StyledEmpty description={formatMessage('common.no_venues_found')} />
          )}
        </ItemList>
      )}
    </Stack>
  );
};
